/**
 * Statutory Exit (PF Form 10 / ESI / Gratuity) API client.
 */
import apiClient from '@/shared/api/client'

export const STATUTORY_TYPES = ['PF', 'ESI', 'GRATUITY'] as const
export type StatutoryType = (typeof STATUTORY_TYPES)[number]

export const STATUTORY_STATUSES = [
  'DATA_READY', 'FILED', 'NOT_APPLICABLE', 'PENDING_INFO', 'HOLD',
] as const
export type StatutoryStatus = (typeof STATUTORY_STATUSES)[number]

export interface StatutoryExitRecord {
  id: string
  separation_id: string
  employee_id: string
  employee_code: string
  emp_name: string | null
  department_name: string | null
  statutory_type: StatutoryType
  exit_date: string
  exit_reason_code: string | null
  last_contribution_year: number | null
  last_contribution_month: number | null
  last_basic_da_wages: number | null
  uan_no: string | null
  pf_no: string | null
  pf_join_date: string | null
  esi_no: string | null
  esi_dispensary: string | null
  esi_join_date: string | null
  form_data: Record<string, unknown> | null
  status: StatutoryStatus
  statutory_deadline: string | null
  filed_at: string | null
  filing_reference: string | null
  missing_fields: string[]
  remarks: string | null
  created_at: string | null
}

export interface StatutorySummary {
  total: number
  pending: number
  filed: number
  not_applicable: number
  overdue: number
  due_within_5_days: number
  by_status: Record<string, number>
  by_type: Record<string, number>
}

const BASE = '/hr/statutory-exit'

export const statutoryExitApi = {
  summary: async () => (await apiClient.get<StatutorySummary>(`${BASE}/summary`)).data,
  list: async (params: {
    status?: StatutoryStatus
    statutory_type?: StatutoryType
    employee_id?: string
    overdue_only?: boolean
    limit?: number
  } = {}) =>
    (await apiClient.get<StatutoryExitRecord[]>(BASE, { params })).data,
  detail: async (id: string) =>
    (await apiClient.get<StatutoryExitRecord>(`${BASE}/${id}`)).data,
  createForSeparation: async (separation_id: string) =>
    (await apiClient.post<{ created: number; schemes: string[] }>(
      `${BASE}/separation/${separation_id}/create`,
    )).data,
  markFiled: async (id: string, body: {
    filing_reference: string
    remarks?: string
  }) => (await apiClient.post<StatutoryExitRecord>(`${BASE}/${id}/mark-filed`, body)).data,
  updateStatus: async (id: string, body: {
    new_status: StatutoryStatus
    remarks?: string
  }) => (await apiClient.post<StatutoryExitRecord>(`${BASE}/${id}/update-status`, body)).data,
}

export const STATUS_COLOR: Record<StatutoryStatus, string> = {
  DATA_READY: 'processing',
  FILED: 'success',
  NOT_APPLICABLE: 'default',
  PENDING_INFO: 'warning',
  HOLD: 'magenta',
}

export const TYPE_COLOR: Record<StatutoryType, string> = {
  PF: 'blue',
  ESI: 'cyan',
  GRATUITY: 'purple',
}
